import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Camera, Eye, Film, Loader2, PhoneCall, ShoppingBag, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { PhotoPicker } from "@/components/PhotoPicker";
import { busyVideo, SavedVideos, VideoPicker } from "@/components/VideoPicker";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { api, errMsg, eur, img } from "@/lib/api";

const AREAS = {
  digestivo: ["Digestivo / cólico", ["Heces blandas o diarrea", "Heces secas o pocas", "Barriga hinchada", "Come menos", "Pierde peso", "Ruidos intestinales raros"]],
  patas: ["Patas, cascos y movimiento", ["Cojea", "Rigidez al empezar a trabajar", "Pata hinchada o caliente", "Casco agrietado o blando", "Le cuesta girar o bajar cuestas", "Se tropieza"]],
  piel: ["Piel, pelo y heridas", ["Picor", "Se rasca contra las paredes", "Calvas o pérdida de pelo", "Costras o granos", "Herida o rozadura", "Crin o cola deteriorada"]],
  respiratorio: ["Respiratorio", ["Tos", "Mocos", "Respira rápido tras el ejercicio", "Se cansa enseguida", "Ruido al respirar"]],
  comportamiento: ["Comportamiento y nervios", ["Nervioso en viajes o concursos", "Asustadizo", "Se muerde o muerde", "Agresivo de repente", "Apático o triste"]],
  estado: ["Energía y estado general", ["Cansado o sin energía", "Pierde músculo", "Pelo apagado", "Suda mucho", "Bebe poco o mucho", "Le cuesta recuperarse"]],
  ojos_boca: ["Ojos, boca y dientes", ["Ojo lloroso o cerrado", "Mastica raro o deja caer comida", "Mal olor de boca", "Babea", "Rechaza el bocado"]],
};
const FLAGS = [["colico", "Signos de cólico (se revuelca, patea la barriga)"], ["no_come", "No come nada desde hace horas"], ["no_defeca", "No defeca"], ["cojera_grave", "No apoya una pata"], ["fiebre", "Fiebre (más de 38,5 °C)"], ["herida_profunda", "Herida profunda o que sangra mucho"], ["respira_mal", "Le cuesta respirar"], ["tumbado", "Está tumbado y no se levanta"]];
const DURATIONS = ["Hoy mismo", "1-3 días", "Una semana", "Más de una semana", "Más de un mes"];
const URG = {
  urgente: ["bg-[#DC2626] text-white", "URGENTE · Llama al veterinario ahora", PhoneCall],
  pronto: ["bg-[#0B1D3A] text-white", "Consulta con tu veterinario en 24-48 h", Stethoscope],
  observar: ["bg-[#EFF6FF] text-[#0B1D3A]", "Leve · Obsérvalo y coméntalo con tu veterinario", Eye],
};
const LIKE = { alta: "bg-[#DC2626] text-white", media: "bg-[#2563EB] text-white", baja: "bg-slate-200 text-slate-700" };

const Product = ({ p }) => {
  const { add } = useCart();
  const [fmt, setFmt] = useState(p.formats[0]);
  return (
    <div className="flex gap-4 bg-white border border-slate-200 rounded p-4" data-testid={`dx-product-${p.slug}`}>
      <img src={img(p.images[0])} alt={p.name} className="w-20 h-20 object-contain bg-[#EFF6FF] rounded" />
      <div className="flex-1 min-w-0">
        <Link to={`/producto/${p.slug}`} className="font-display font-extrabold text-xl text-[#0B1D3A] hover:text-[#DC2626]">{p.name}</Link>
        <p className="text-sm text-slate-600">{p.why}</p>
        <p className="text-sm mt-1"><b className="text-[#1E3A8A]">Uso:</b> {p.dose}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {p.formats.map((f) => <button key={f.id} onClick={() => setFmt(f)} className={`text-xs px-2.5 py-1 rounded-full border ${fmt.id === f.id ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300"}`}>{f.label}</button>)}
          <span className="font-bold text-[#DC2626] ml-1">{eur(fmt.price)}</span>
        </div>
      </div>
      <button onClick={() => add(p, fmt)} data-testid={`dx-add-${p.slug}`} aria-label="Añadir" className="self-center bg-[#DC2626] hover:bg-[#B91C1C] text-white p-3 rounded-full"><ShoppingBag size={17} /></button>
    </div>
  );
};

const VideoFindings = ({ r, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm("¿Eliminar ahora los vídeos de este diagnóstico?")) return;
    setBusy(true);
    const res = await Promise.allSettled(r.videos.map((id) => api.delete(`/dx-video/${id}`)));
    setBusy(false);
    if (res.some((x) => x.status === "rejected")) return toast.error("No se han podido eliminar todos los vídeos. Inténtalo de nuevo.");
    toast.success("Vídeos eliminados"); onDeleted();
  };
  return (
    <div className="border-2 border-[#2563EB]/30 rounded p-5" data-testid="dx-video-findings">
      <h3 className="font-display font-extrabold text-2xl text-[#0B1D3A] flex items-center gap-2"><Film size={22} /> Lo observado en el vídeo</h3>
      {r.video_observed && <p className="text-slate-700 mt-2" data-testid="dx-video-observed">{r.video_observed}</p>}
      {r.movement.length > 0 && <ul className="mt-3 grid sm:grid-cols-2 gap-2">{r.movement.map((m, i) => (
        <li key={i} className="bg-[#EFF6FF] rounded p-3 text-sm" data-testid={`dx-movement-${i}`}><b className="text-[#0B1D3A]">{m.aspect}:</b> {m.finding}</li>))}</ul>}
      <p className="text-xs text-slate-500 mt-3">Guardamos {r.videos.length > 1 ? "los vídeos" : "el vídeo"} en tu cuenta de forma privada 30 días. <button type="button" disabled={busy} onClick={del} className="font-bold text-[#DC2626] underline disabled:opacity-50" data-testid="dx-video-delete-all">{busy ? "Eliminando…" : "Eliminar ahora"}</button></p>
    </div>
  );
};

const Result = ({ r, onVideosDeleted }) => {
  const [cls, label, Icon] = URG[r.urgency];
  return (
    <div className="space-y-6 rise" data-testid="dx-result">
      <div className={`rounded p-5 flex items-center gap-3 ${cls}`} data-testid={`dx-urgency-${r.urgency}`}><Icon size={26} /><p className="font-display font-black text-2xl">{label}</p></div>
      {r.video_warning && <p className="text-sm bg-[#FEF3C7] text-[#92400E] rounded p-3 flex gap-2" data-testid="dx-video-warning"><AlertTriangle size={18} className="shrink-0" /> {r.video_warning}</p>}
      {r.red_flags.length > 0 && <p className="text-sm text-[#DC2626] font-semibold" data-testid="dx-red-flags">Has marcado señales de alarma: {r.red_flags.join(" · ")}. No esperes: llama al veterinario.</p>}
      <div><h3 className="font-display font-extrabold text-2xl text-[#0B1D3A]">Nuestra opinión</h3>
        {r.observed && <p className="text-slate-700 mt-2" data-testid="dx-observed"><b className="text-[#0B1D3A]">Lo que vemos en la foto:</b> {r.observed}</p>}
        {r.followup_id && <p className="mt-2 text-sm bg-[#EFF6FF] text-[#0B1D3A] rounded p-3" data-testid="dx-followup-saved">Hemos guardado la foto en tu cuenta. En 7 días te recordaremos por email subir otra para comparar la evolución. <Link to="/cuenta?tab=seguimiento" className="font-bold text-[#2563EB] underline">Ver seguimiento</Link></p>}
        {r.photo_request && <p className="mt-2 text-sm bg-[#FEF3C7] text-[#92400E] rounded p-3 flex gap-2" data-testid="dx-photo-request"><Camera size={18} className="shrink-0" /> {r.photo_request}</p>}
        <p className="text-slate-700 mt-2" data-testid="dx-summary">{r.summary}</p></div>
      {r.videos?.length > 0 && <VideoFindings r={r} onDeleted={onVideosDeleted} />}
      <div>
        <h3 className="font-display font-extrabold text-2xl text-[#0B1D3A]">Posibles causas</h3>
        <div className="space-y-2 mt-3">{r.possible_causes.map((c, i) => (
          <div key={i} className="border border-slate-200 rounded p-4" data-testid={`dx-cause-${i}`}>
            <div className="flex flex-wrap gap-2 items-center"><p className="font-bold text-[#0B1D3A]">{c.name}</p><span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${LIKE[c.likelihood] || LIKE.baja}`}>Probabilidad {c.likelihood}</span></div>
            <p className="text-sm text-slate-600 mt-1">{c.explanation}</p>
          </div>
        ))}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#EFF6FF] rounded p-5"><h3 className="font-display font-extrabold text-xl text-[#0B1D3A]">Qué puedes hacer ya</h3>
          <ul className="mt-2 space-y-1.5 text-sm">{r.immediate_steps.map((s) => <li key={s} className="flex gap-2"><span className="text-[#DC2626] font-bold">→</span>{s}</li>)}</ul></div>
        <div className="border border-slate-200 rounded p-5"><h3 className="font-display font-extrabold text-xl text-[#0B1D3A]">Vigila si empeora</h3>
          <ul className="mt-2 space-y-1.5 text-sm">{r.watch_for.map((s) => <li key={s} className="flex gap-2"><AlertTriangle size={15} className="text-[#DC2626] shrink-0 mt-0.5" />{s}</li>)}</ul></div>
      </div>
      <div className="bg-[#0B1D3A] text-white rounded p-6" data-testid="dx-vet-box">
        <p className="flex items-center gap-2 font-display font-black text-2xl"><PhoneCall size={22} /> Llama a tu veterinario</p>
        <p className="text-blue-100 mt-2 text-sm">{r.vet_advice}</p>
        <p className="text-xs text-blue-200 mt-3">{r.vet_always}</p>
      </div>
      {r.products.length > 0 && (
        <div><h3 className="font-display font-extrabold text-2xl text-[#0B1D3A]">Productos FAUNNY de apoyo</h3>
          <p className="text-sm text-slate-500">Complementos para acompañar las indicaciones del veterinario, no para sustituirlas.</p>
          <div className="grid lg:grid-cols-2 gap-3 mt-3">{r.products.map((p) => <Product key={p.slug} p={p} />)}</div></div>
      )}
    </div>
  );
};

export default function Diagnosis() {
  const [area, setArea] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [flags, setFlags] = useState([]);
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [f, setF] = useState({ duration: "", age: "", activity: "", description: params.get("producto") ? `Estoy mirando ${params.get("producto").replace(/-/g, " ")}. ¿Le conviene a mi caballo?` : "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [videos, setVideos] = useState([]);
  const toggle = (list, set, v) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async (e) => {
    e.preventDefault();
    if (videos.some(busyVideo)) return toast.error("Espera a que terminen de subirse y analizarse los vídeos");
    const videoIds = videos.filter((v) => v.status === "ready").map((v) => v.server.id);
    if (videos.some((v) => v.status === "error")) toast.info("Los vídeos con error no se usarán en el diagnóstico");
    if (!photo && !videoIds.length && !area) return toast.error("Elige qué zona o problema tiene tu caballo, o sube una foto");
    if (!photo && !videoIds.length && !symptoms.length && !f.description.trim()) return toast.error("Marca algún síntoma, descríbelo o sube una foto");
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/diagnosis", { area: area || "general", symptoms, red_flags: flags, ...f, duration: f.duration || null, age: f.age || null, activity: f.activity || null, photo, video_ids: videoIds });
      setRes(data);
      if (data.videos?.length) setVideos([]);
      setTimeout(() => document.getElementById("dx-result-anchor")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <div data-testid="diagnosis-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="text-[#60A5FA] font-bold text-sm tracking-[0.2em] uppercase">Gratis · Con IA · Sin cuenta</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl mt-2">Diagnóstico FAUNNY</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Cuéntanos qué le pasa a tu caballo o súbenos una foto y te damos nuestra opinión sobre las posibles causas, qué hacer ya y cuándo llamar al veterinario. Siempre recomendamos que lo confirme tu veterinario.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-12 grid lg:grid-cols-[440px_1fr] gap-10">
        <form onSubmit={submit} className="space-y-6">
          <PhotoPicker photo={photo} setPhoto={setPhoto} note={user ? "Guardaremos la foto en tu cuenta para que puedas comparar su evolución; solo tú la verás y puedes borrarla." : "La foto solo se usa para este análisis y no se guarda. Inicia sesión para guardarla y hacer seguimiento."} />
          <VideoPicker user={user} videos={videos} setVideos={setVideos} />
          {user && <SavedVideos refresh={res} />}
          <div>
            <Label className="font-display text-lg font-bold text-[#0B1D3A]">1. ¿Dónde está el problema? <span className="text-sm font-normal text-slate-500">(opcional si subes foto)</span></Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {Object.entries(AREAS).map(([k, [l]]) => (
                <button type="button" key={k} onClick={() => { setArea(k); setSymptoms([]); }} data-testid={`dx-area-${k}`}
                  className={`text-left text-sm font-semibold p-3 rounded border-2 transition-colors ${area === k ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-200 hover:border-[#2563EB]"}`}>{l}</button>
              ))}
            </div>
          </div>
          {area && (
            <div>
              <Label className="font-display text-lg font-bold text-[#0B1D3A]">2. ¿Qué notas?</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {AREAS[area][1].map((s, i) => (
                  <button type="button" key={s} onClick={() => toggle(symptoms, setSymptoms, s)} data-testid={`dx-symptom-${i}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${symptoms.includes(s) ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-slate-300 hover:border-[#2563EB]"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label>Cuéntanos más (opcional)</Label>
            <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Ej.: desde hace 3 días cojea de la mano derecha al trote, sobre todo en suelo duro…" data-testid="dx-description" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Desde cuándo</Label>
              <Select value={f.duration} onValueChange={(v) => setF({ ...f, duration: v })}><SelectTrigger className="mt-1.5" data-testid="dx-duration"><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Edad</Label>
              <Select value={f.age} onValueChange={(v) => setF({ ...f, age: v })}><SelectTrigger className="mt-1.5" data-testid="dx-age"><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>{["Potro", "Adulto", "Senior (+18 años)"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="border-2 border-[#DC2626]/30 rounded p-4">
            <p className="font-display font-bold text-lg text-[#0B1D3A]">¿Hay alguna señal de alarma?</p>
            <div className="space-y-2 mt-2">{FLAGS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer"><Checkbox checked={flags.includes(k)} onCheckedChange={() => toggle(flags, setFlags, k)} data-testid={`dx-flag-${k}`} />{l}</label>
            ))}</div>
          </div>
          {flags.length > 0 && <p className="text-sm font-bold text-white bg-[#DC2626] rounded p-3" data-testid="dx-flag-warning">Con estas señales llama ya al veterinario. Mientras llega, te damos nuestra opinión.</p>}
          <button disabled={busy} data-testid="dx-submit" className="w-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white font-display font-bold text-lg py-3.5 rounded-full flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="animate-spin" size={18} /> Analizando…</> : videos.some(busyVideo) ? "Procesando vídeo…" : "Ver nuestra opinión"}
          </button>
        </form>
        <div id="dx-result-anchor">
          {res ? <Result r={res} onVideosDeleted={() => setRes({ ...res, videos: [] })} /> : (
            <div className="border-2 border-dashed border-slate-200 rounded p-10 text-center text-slate-500 h-full grid place-items-center" data-testid="dx-placeholder">
              {busy ? "Dr. FAUNNY está revisando los síntomas…" : "Elige la zona, marca lo que notas y pulsa “Ver nuestra opinión”."}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-6">Opinión orientativa generada con IA. No es un diagnóstico veterinario ni sustituye la exploración de un profesional. Ante cualquier duda, llama a tu veterinario.</p>
        </div>
      </div>
    </div>
  );
}
